import { useEffect, useState } from "react";
import { PenLine, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-neutral-700 bg-black px-3 py-2 text-sm text-stone-100 outline-none focus:border-white";
const GROUPS = [
  ["Hero (top of homepage)", [["hero_kicker", "Small line above headline"], ["hero_title_1", "Headline line 1"], ["hero_title_2", "Headline line 2"], ["slogan", "Slogan under headline (blank = default)"], ["hero_cta_primary", "Primary button text"], ["hero_cta_secondary", "Secondary button text"]]],
  ["Announcement bar", [["announcement", "Message shown at the very top of every page (blank = hidden)"]]],
  ["Marquee", [["marquee", "Comma-separated words"]]],
  ["Stats", [["stat_1_value", "Stat 1 value"], ["stat_1_label", "Stat 1 label"], ["stat_2_value", "Stat 2 value"], ["stat_2_label", "Stat 2 label"], ["stat_3_value", "Stat 3 value"], ["stat_3_label", "Stat 3 label"], ["stat_4_value", "Stat 4 value"], ["stat_4_label", "Stat 4 label"]]],
  ["About section", [["about_kicker", "Kicker"], ["about_title", "Title"], ["about_body", "Body", true]]],
  ["Business section", [["biz_kicker", "Kicker"], ["biz_title", "Title"], ["biz_body", "Body", true]]],
  ["Services & products section", [["products_kicker", "Kicker"], ["products_title", "Title"], ["products_body", "Body", true]]],
  ["Artists section", [["artists_title", "Title"], ["artists_body", "Body", true]]],
];

export const ContentPanel = () => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/site/content").then((r) => setC(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load — please refresh.")); }, []);
  if (err) return <p className="mt-6 text-red-400" data-testid="panel-error">{err}</p>;
  if (!c) return <p className="mt-6 text-neutral-500">Loading…</p>;
  const set = (k) => (e) => setC((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/site/content", c); setC(data); toast.success("Website text updated — changes are live."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset all homepage text to the original copy?")) return;
    const { data } = await api.post("/admin/site/content/reset"); setC(data); toast.success("Reset to defaults.");
  };
  return (
    <div className="mt-6" data-testid="content-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-display text-lg uppercase text-stone-100"><PenLine className="h-5 w-5" /> Website text</div>
        <div className="flex gap-2">
          <button onClick={reset} data-testid="content-reset-btn" className="inline-flex items-center gap-1 rounded-full border border-neutral-700 px-4 py-2 text-xs text-neutral-300 hover:border-white"><RotateCcw className="h-3.5 w-3.5" /> Reset</button>
          <button onClick={save} disabled={busy} data-testid="content-save-btn" className="btn-gold rounded-full px-6 py-2 font-display uppercase disabled:opacity-60">{busy ? "Saving…" : "Publish changes"}</button>
        </div>
      </div>
      <p className="mt-1 text-xs text-neutral-500">Edit any text on the homepage. Changes go live the moment you publish. Images, video and music are managed in the Media tab; business details in Company.</p>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {GROUPS.map(([title, fields]) => (
          <div key={title} className="rounded-2xl border border-neutral-800 bg-[#0a0a0a] p-5" data-testid={`content-group-${title.split(" ")[0].toLowerCase()}`}>
            <h3 className="font-cond text-base font-bold uppercase tracking-wide text-stone-100">{title}</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {fields.map(([k, l, long]) => (
                <label key={k} className={`font-mono-label text-[10px] uppercase text-neutral-500 ${long ? "sm:col-span-2" : ""}`}>{l}
                  {long ? <textarea rows={4} value={c[k]} onChange={set(k)} data-testid={`content-${k}`} className={`${inputCls} mt-1`} />
                    : <input value={c[k]} onChange={set(k)} data-testid={`content-${k}`} className={`${inputCls} mt-1`} />}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
